import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { ReactElement, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  clearCourseMigrationStatus,
  fetchCourseMigratableContent,
  fetchCourseV2Compliances,
  fetchCourseV2QuizThunk,
  fetchCourseV2QuizQuestionsThunk,
  migrateCourseV2Quiz,
  selectCourseMigrationError,
  selectCourseMigrationStatus,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'

type Props = {
  isOpen: boolean
  onClose: () => void
  courseID: string
}

export default function MigrateQuizModal({
  isOpen,
  onClose,
  courseID,
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let migrationStatus = useAppSelector(selectCourseMigrationStatus)
  let migrationError = useAppSelector(selectCourseMigrationError)

  let isLoading = migrationStatus === 'pending'

  useEffect(() => {
    if (migrationStatus === 'fulfilled') {
      dispatch(fetchCourseMigratableContent(courseID))
      dispatch(fetchCourseV2Compliances())
      dispatch(fetchCourseV2QuizThunk(courseID))
      dispatch(fetchCourseV2QuizQuestionsThunk(courseID))
      dispatch(clearCourseMigrationStatus())
      onClose()
    }
  }, [migrationStatus, courseID, dispatch, onClose])

  let handleClose = () => {
    dispatch(clearCourseMigrationStatus())
    onClose()
  }

  let handleConfirm = () => {
    dispatch(migrateCourseV2Quiz(courseID))
  }

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: modalPaperProps }}
    >
      <DialogTitle>Migrate Quiz to v2</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          This will migrate the legacy Quiz v1 to Quiz v2 for this course.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The quiz compliance field will be set to <strong>Updated</strong> and
          will require review.
        </Typography>
        {migrationError && (
          <Typography variant="body2" color="error">
            {migrationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? 'Migrating…' : 'Migrate Quiz'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
